'use client';

import React from 'react';
import { useParams } from 'react-router-dom';
import { Store } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useFunnelsList, useSetDefaultFunnel } from '@/lib/api/funnels';
import { FunnelsTable } from './funnels-table-components/FunnelsTable';
import { useDeleteFunnelsDialog } from './funnels-table-components/useDeleteFunnelsDialog';
import { FunnelQuickstartWizard } from '@/components/funnels/FunnelQuickstartWizard';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

/**
 * Storefront list page
 * Displays all funnels with the new storefront editor interface
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(StorefrontListPage, PERMISSIONS.STORES_VIEW);

function StorefrontListPage() {
  const { id } = useParams();
  const { storeRoutes, navigate } = useStoreNavigate();
  const { t } = useTranslation('storefront');

  const {
    data: funnelsResponse,
    isLoading: loading,
    error,
    refetch,
  } = useFunnelsList(id || '');

  const funnels = funnelsResponse?.funnels || [];
  const total = funnelsResponse?.total || 0;
  const pageCount = funnelsResponse?.pageCount || 0;

  // Template dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = React.useState(false);

  // Mutations
  const setDefaultMutation = useSetDefaultFunnel();

  // Delete (with demote-first for live funnels) is handled by a shared dialog.
  const deleteDialog = useDeleteFunnelsDialog(id || '', refetch);

  // Memoized stats cards configuration - Hidden
  // const statsCards = React.useMemo(
  //   () => [
  //     {
  //       title: 'Total Funnels',
  //       value: total,
  //       description: 'All funnels',
  //       icon: GitBranch,
  //     },
  //     {
  //       title: 'Active Funnels',
  //       value: funnels.filter((f: Funnel) => f.status === 'active').length,
  //       description: 'Currently active',
  //       icon: GitBranch,
  //     },
  //     {
  //       title: 'Avg Conversion Rate',
  //       value:
  //         funnels.length > 0
  //           ? `${(funnels.reduce((acc: number, f: Funnel) => acc + (f.conversionRate || 0), 0) / funnels.length).toFixed(1)}%`
  //           : '0%',
  //       description: 'Across all funnels',
  //       icon: TrendingUp,
  //     },
  //     {
  //       title: 'Total Steps',
  //       value: funnels.reduce((acc: number, f: Funnel) => acc + (f.stepsCount || 0), 0),
  //       description: 'Combined steps',
  //       icon: GitBranch,
  //     },
  //   ],
  //   [funnels, total],
  // );

  // Handle create funnel - Open template selection dialog
  const handleCreateFunnel = () => {
    setTemplateDialogOpen(true);
  };

  // Handle funnel created from template
  const handleFunnelCreated = (funnel: { id: string; name: string }, templateId: string) => {
    // Navigate to new storefront editor with tour trigger and template info
    navigate(`${storeRoutes.STOREFRONT_EDITOR(funnel.id)}?tour=setup&template=${templateId}`);
  };

  // Handle edit funnel - Navigate to new storefront editor
  const handleEditFunnel = (funnelId: string) => {
    if (!id) return;
    navigate(storeRoutes.STOREFRONT_EDITOR(funnelId));
  };

  // Handle delete funnel — opens the confirm dialog, which demotes a live funnel
  // before deleting it (the server blocks deleting a promoted funnel).
  const handleDeleteFunnel = (funnelId: string) => {
    const funnel = funnels.find((f) => f.id === funnelId);
    if (funnel) deleteDialog.open([funnel]);
  };

  // Handle set as default
  const handleSetAsDefault = async (funnelId: string) => {
    if (!id) return;

    try {
      await setDefaultMutation.mutateAsync({
        funnelId,
        storeId: id,
        isDefault: true,
      });

      toast.success(t('funnelsPage.funnelSetAsDefault'));
      refetch();
    } catch (error) {
      toast.error(t('funnelsPage.failedSetDefault'));
      console.error('Set as default error:', error);
    }
  };

  // Early return after all hooks
  if (!id) {
    return <div>{t('funnelsPage.storeIdNotFound')}</div>;
  }

  // Type assertion for id since we've checked it's not undefined
  const storeId = id as string;

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl font-semibold sm:text-2xl">{t('funnelsPage.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('funnelsPage.subtitle')}</p>
          </div>
        </div>
      </div>
      <Card>
        <CardContent>
          <FunnelsTable
            storeId={storeId}
            funnels={funnels}
            total={total}
            pageCount={pageCount}
            isLoading={loading}
            onTableStateChange={() => {}}
            onRefetch={refetch}
            onCreateFunnel={handleCreateFunnel}
            onEditFunnel={handleEditFunnel}
            onDeleteFunnel={handleDeleteFunnel}
            onSetAsDefault={handleSetAsDefault}
          />
          {deleteDialog.dialog}
          {error && (
            <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
              {error instanceof Error ? error.message : t('funnelsPage.failedLoadFunnels')}
            </div>
          )}
        </CardContent>
      </Card>
      <FunnelQuickstartWizard
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        storeId={storeId}
        onSuccess={handleFunnelCreated}
      />
    </div>
  );
}
